import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, downloadPdf } from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, UserSquare2, FileText, Wallet } from "lucide-react";
import AmountMonthsInput from "@/components/AmountMonthsInput";
import SearchInput from "@/components/SearchInput";
import Pagination, { paginate } from "@/components/Pagination";
import { toast } from "sonner";

const empty = { nom: "", telephone: "", email: "", piece_identite: "", maison_id: "", plan_id: "", date_entree: "", date_sortie: "", loyer_convenu: 0, caution: 0, avance_initiale: 0, statut: "actif", notes: "", tax_habitation_active: true, is_temporary: false, contract_template_id: "" };

export default function Locataires() {
  const [items, setItems] = useState([]);
  const [maisons, setMaisons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [avanceOpen, setAvanceOpen] = useState(false);
  const [avanceLoc, setAvanceLoc] = useState(null);
  const [avanceForm, setAvanceForm] = useState({ montant: 0, notes: "" });
  const [plans, setPlans] = useState([]); // plans of selected maison
  const [contractTemplates, setContractTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    const [l, m] = await Promise.all([api.get("/locataires"), api.get("/maisons")]);
    setItems(l.data); setMaisons(m.data);
    try {
      const { data } = await api.get("/contract-templates");
      setContractTemplates(data || []);
    } catch { setContractTemplates([]); }
  };
  useEffect(() => { load(); }, []);

  const maisonName = (id) => maisons.find((x) => x.id === id)?.nom || "—";

  const filteredItems = items.filter((l) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (l.nom || "").toLowerCase().includes(q) ||
      (l.telephone || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.plan_id || "").toLowerCase().includes(q) ||
      (maisonName(l.maison_id) || "").toLowerCase().includes(q)
    );
  });
  const pag = paginate(filteredItems, page);
  useEffect(() => { setPage(1); }, [search]);

  // Load plans when maison changes inside the dialog
  const loadPlans = async (maisonId) => {
    if (!maisonId) { setPlans([]); return; }
    try {
      const { data } = await api.get(`/maisons/${maisonId}/plans`);
      setPlans(data.plans || []);
    } catch { setPlans([]); }
  };

  useEffect(() => {
    if (open) loadPlans(form.maison_id);
  }, [open, form.maison_id]);

  const onMaisonChange = (mid) => {
    // Reset plan + price when maison changes
    setForm((f) => ({ ...f, maison_id: mid, plan_id: "", loyer_convenu: 0 }));
  };

  const onPlanChange = (planId) => {
    const plan = plans.find((p) => p.plan_id === planId);
    setForm((f) => ({
      ...f,
      plan_id: planId,
      loyer_convenu: plan ? plan.prix : f.loyer_convenu,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        loyer_convenu: Number(form.loyer_convenu || 0),
        caution: Number(form.caution || 0),
        avance_initiale: Number(form.avance_initiale || 0),
        maison_id: form.maison_id || null,
        plan_id: form.plan_id || null,
        date_entree: form.date_entree || null,
        date_sortie: form.date_sortie || null,
        tax_habitation_active: form.tax_habitation_active !== false,
        is_temporary: form.is_temporary === true,
        contract_template_id: form.contract_template_id || null,
      };
      if (editing) await api.put(`/locataires/${editing}`, payload);
      else await api.post("/locataires", payload);
      toast.success(editing ? "Locataire modifié" : "Locataire créé");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
  };

  const onEdit = (it) => {
    setEditing(it.id);
    setForm({
      ...empty, ...it,
      maison_id: it.maison_id || "",
      date_entree: it.date_entree || "",
      date_sortie: it.date_sortie || "",
      tax_habitation_active: it.tax_habitation_active !== false,
      is_temporary: it.is_temporary === true,
      contract_template_id: it.contract_template_id || "",
    });
    setOpen(true);
  };
  const onDelete = async () => {
    try { await api.delete(`/locataires/${confirmId}`); toast.success("Supprimé"); setConfirmId(null); load(); }
    catch { toast.error("Suppression échouée"); }
  };

  const openAvance = (loc) => {
    setAvanceLoc(loc);
    setAvanceForm({ montant: 0, notes: "" });
    setAvanceOpen(true);
  };

  const submitAvance = async (e) => {
    e.preventDefault();
    const montant = Number(avanceForm.montant || 0);
    if (!montant || montant <= 0) { toast.error("Montant invalide"); return; }
    try {
      await api.post(`/locataires/${avanceLoc.id}/avance`, { montant, notes: avanceForm.notes });
      toast.success(`Avance ajoutée : +${formatFCFA(montant)}`);
      setAvanceOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div>
      <PageHeader
        title="Locataires"
        subtitle="Liste des locataires actifs et historiques, avec leur bien associé et leur loyer convenu."
        testid="page-locataires"
        accent="violet"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher locataire…" testid="search-locataires" />
            <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-orange-500 hover:bg-orange-600 text-white shadow-md shadow-orange-900/20" data-testid="btn-add-locataire">
              <Plus className="w-4 h-4 mr-2" /> Nouveau locataire
            </Button>
          </div>
        }
      />

      <Card className="rounded-xl border border-slate-200 shadow-sm overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Téléphone</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Maison</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Plan</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Entrée</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Loyer</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Avance restante</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-12">
                <UserSquare2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                {search ? `Aucun résultat pour « ${search} ».` : "Aucun locataire enregistré."}
              </TableCell></TableRow>
            )}
            {pag.rows.map((l) => (
              <TableRow key={l.id} className="hover:bg-slate-50/60">
                <TableCell className="font-medium text-slate-900">{l.nom}</TableCell>
                <TableCell className="text-slate-600">{l.telephone || "—"}</TableCell>
                <TableCell className="text-slate-600">{maisonName(l.maison_id)}</TableCell>
                <TableCell>
                  {l.plan_id ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 font-mono">{l.plan_id}</Badge>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-slate-600">{formatDate(l.date_entree)}</TableCell>
                <TableCell className="text-right font-medium">{formatFCFA(l.loyer_convenu)}</TableCell>
                <TableCell className="text-right" data-testid={`avance-restante-${l.id}`}>
                  {(l.avance_restante || 0) > 0 ? (
                    <span className="font-medium text-emerald-700">{formatFCFA(l.avance_restante)}</span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {l.statut === "actif"
                    ? <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">Actif</Badge>
                    : <Badge className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100">Sorti</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openAvance(l)} title="Ajouter une avance" data-testid={`btn-add-avance-${l.id}`}>
                    <Wallet className="w-4 h-4 text-emerald-700" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => downloadPdf(`/locataires/${l.id}/contrat`).catch(() => toast.error("Erreur téléchargement"))} title="Contrat de bail" data-testid={`btn-contrat-${l.id}`}>
                    <FileText className="w-4 h-4 text-emerald-700" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => onEdit(l)} data-testid={`btn-edit-loc-${l.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmId(l.id)} data-testid={`btn-delete-loc-${l.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <Pagination {...pag} onChange={setPage} testid="pagination-locataires" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="dialog-locataire">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Modifier locataire" : "Nouveau locataire"}</DialogTitle></DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start justify-between gap-4" data-testid="locataire-temporary-toggle">
                <div>
                  <Label className="text-sm font-semibold text-slate-800">Locataire temporaire</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Court séjour : désactive le calcul mensuel automatique du loyer. La caution et l'avance couvrent la totalité du séjour et sont enregistrées comme paiements manuels.
                  </p>
                </div>
                <Switch
                  checked={form.is_temporary === true}
                  onCheckedChange={(v) => setForm({ ...form, is_temporary: v })}
                  data-testid="switch-loc-temporary"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Nom complet *</Label>
                <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} required data-testid="input-loc-nom" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Pièce d'identité (CNI / Passeport)</Label>
                <Input value={form.piece_identite} onChange={(e) => setForm({ ...form, piece_identite: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Maison</Label>
                <Select value={form.maison_id || "_none"} onValueChange={(v) => onMaisonChange(v === "_none" ? "" : v)}>
                  <SelectTrigger data-testid="select-loc-maison"><SelectValue placeholder="Sélectionner une maison" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Aucune</SelectItem>
                    {maisons.map((m) => <SelectItem key={m.id} value={m.id}>{m.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.maison_id && plans.length > 0 && (
                <div className="space-y-2 col-span-2">
                  <Label>Plan / Architecture *</Label>
                  <Select value={form.plan_id || ""} onValueChange={onPlanChange}>
                    <SelectTrigger data-testid="select-loc-plan">
                      <SelectValue placeholder="Choisir un plan disponible" />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((p) => {
                        const disabled = p.occupied && p.occupant_id !== editing;
                        return (
                          <SelectItem
                            key={p.plan_id}
                            value={p.plan_id}
                            disabled={disabled}
                            data-testid={`plan-option-${p.plan_id}`}
                          >
                            <span className="font-mono font-semibold text-emerald-700 mr-2">{p.plan_id}</span>
                            <span className="text-slate-600">{p.archi_type}</span>
                            <span className="text-slate-400 mx-2">·</span>
                            <span className="font-medium">{formatFCFA(p.prix)}</span>
                            {disabled && <span className="ml-2 text-xs text-rose-600">— Occupé par {p.occupant_nom}</span>}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Lettre = architecture (A, B, C…) · numéro = plan disponible. Le prix de location est rempli automatiquement.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Date d'entrée</Label>
                <Input type="date" value={form.date_entree?.slice(0,10) || ""} onChange={(e) => setForm({ ...form, date_entree: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Date de sortie</Label>
                <Input type="date" value={form.date_sortie?.slice(0,10) || ""} onChange={(e) => setForm({ ...form, date_sortie: e.target.value })} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Loyer convenu (FCFA)</Label>
                <Input type="number" min="0" value={form.loyer_convenu} onChange={(e) => setForm({ ...form, loyer_convenu: e.target.value })} data-testid="input-loc-loyer" />
              </div>
              <div className="col-span-2">
                <AmountMonthsInput
                  label="Caution"
                  prix={Number(form.loyer_convenu || 0)}
                  value={form.caution}
                  onChange={(v) => setForm({ ...form, caution: v })}
                  amountTestid="input-loc-caution-amount"
                  monthsTestid="input-loc-caution-months"
                  disableMonths={form.is_temporary === true}
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>
                  Avance{editing && (
                    <span className="text-slate-500 font-normal ml-2 text-xs">
                      Restant : <span className="font-semibold text-emerald-700">{formatFCFA(items.find((x) => x.id === editing)?.avance_restante || 0)}</span>
                    </span>
                  )}
                </Label>
                <AmountMonthsInput
                  label=""
                  prix={Number(form.loyer_convenu || 0)}
                  value={form.avance_initiale}
                  onChange={(v) => setForm({ ...form, avance_initiale: v })}
                  amountTestid="input-loc-avance"
                  monthsTestid="input-loc-avance-months"
                  disableMonths={form.is_temporary === true}
                />
                <p className="text-xs text-slate-500">Déduit automatiquement quand vous enregistrez un paiement avec la méthode « Avance ».</p>
              </div>
              <div className="col-span-2 rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 flex items-start justify-between gap-4" data-testid="locataire-tax-toggle">
                <div>
                  <Label className="text-sm font-semibold text-slate-800">Taxe d'habitation applicable</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Si activée, la taxe configurée pour la société est déduite du loyer sur les reçus PDF et le net perçu.
                  </p>
                </div>
                <Switch
                  checked={form.tax_habitation_active !== false}
                  onCheckedChange={(v) => setForm({ ...form, tax_habitation_active: v })}
                  data-testid="switch-loc-tax"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Statut</Label>
                <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="actif">Actif</SelectItem>
                    <SelectItem value="sorti">Sorti</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Contrat à utiliser</Label>
                <Select
                  value={form.contract_template_id || "__default__"}
                  onValueChange={(v) => setForm({ ...form, contract_template_id: v === "__default__" ? "" : v })}
                >
                  <SelectTrigger data-testid="select-loc-contract-template"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Contrat de la maison (par défaut)</SelectItem>
                    {contractTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Par défaut, le contrat associé à la maison est utilisé. Sélectionnez un autre modèle pour générer le PDF de bail avec des clauses spécifiques pour ce locataire.</p>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-loc">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={avanceOpen} onOpenChange={setAvanceOpen}>
        <DialogContent data-testid="dialog-avance">
          <DialogHeader>
            <DialogTitle className="font-heading">Ajouter une avance</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAvance} className="space-y-4">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3">
              <div className="text-xs uppercase tracking-[0.16em] font-bold text-emerald-700/80 mb-1">Locataire</div>
              <div className="font-medium text-slate-900">{avanceLoc?.nom}</div>
              <div className="text-xs text-slate-600 mt-2">
                Solde actuel :{" "}
                <span className="font-semibold text-emerald-700">{formatFCFA(avanceLoc?.avance_restante || 0)}</span>
                {avanceLoc?.loyer_convenu ? (
                  <>
                    {" · Loyer mensuel : "}
                    <span className="font-medium">{formatFCFA(avanceLoc.loyer_convenu)}</span>
                  </>
                ) : null}
              </div>
            </div>
            <AmountMonthsInput
              label="Montant à ajouter *"
              prix={Number(avanceLoc?.loyer_convenu || 0)}
              value={avanceForm.montant}
              onChange={(v) => setAvanceForm({ ...avanceForm, montant: v })}
              amountTestid="input-avance-montant"
              monthsTestid="input-avance-months"
              helpText="Saisissez soit le montant total, soit le nombre de mois — l'autre champ se calcule automatiquement."
              disableMonths={avanceLoc?.is_temporary === true}
            />
            <div className="space-y-2">
              <Label>Notes (facultatif)</Label>
              <Textarea
                rows={2}
                value={avanceForm.notes}
                onChange={(e) => setAvanceForm({ ...avanceForm, notes: e.target.value })}
                placeholder="Réf, mode de paiement, etc."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAvanceOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-avance">
                Ajouter l'avance
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
